import React, { useState, useEffect } from 'react';
import { Bot, Save, AlertCircle, Check, Star } from 'lucide-react';
import { fetchGlobalModelsSettings, updateGlobalModelsSettings, fetchTopModelsSettings, updateTopModelsSettings } from '../services/modelsApi';
import { usePermissions } from '../contexts/PermissionsContext';

const ModelsSettings = ({ models }) => {
  const [globalModels, setGlobalModels] = useState([]);
  const [topModels, setTopModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { hasPermission } = usePermissions();
  
  const canManageModels = hasPermission('MANAGE_SETTINGS');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [globalSettings, topSettings] = await Promise.all([
        fetchGlobalModelsSettings(),
        fetchTopModelsSettings()
      ]);
      setGlobalModels(globalSettings.globalModels || []);
      setTopModels(topSettings.topModels || []);
    } catch (error) {
      console.error('Failed to load models settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModelToggle = (modelId) => {
    if (!canManageModels) return;
    
    setGlobalModels(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };

  const handleTopModelToggle = (modelId) => {
    if (!canManageModels) return;
    
    setTopModels(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId);
      } else {
        // Limit to 6 top models maximum
        const newTopModels = [...prev, modelId];
        if (newTopModels.length > 6) {
          return newTopModels.slice(-6); // Keep only the last 6
        }
        return newTopModels;
      }
    });
  };

  const handleSave = async () => {
    if (!canManageModels) return;
    
    try {
      setSaving(true);
      await Promise.all([
        updateGlobalModelsSettings({ globalModels }),
        updateTopModelsSettings({ topModels })
      ]);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save models settings:', error);
      alert('Failed to save models settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading models settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Bot className="w-5 h-5" />
          Model Configuration
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure global model access and select top models for the dropdown.
        </p>
      </div>

      {!canManageModels && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            You don't have permission to manage global models. Contact an administrator to make changes.
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li><strong>Global Access:</strong> Models available to ALL users by default</li>
            <li><strong>Top Models:</strong> Up to 6 models shown first in the dropdown (marked with ⭐)</li>
            <li>Teams can add additional models beyond these global settings</li>
            <li>Users will see both global models + their team's additional models</li>
          </ul>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          {Object.entries(models).filter(([provider, modelList]) => 
            Array.isArray(modelList) && modelList.length > 0
          ).map(([provider, modelList]) => (
            <div key={provider}>
              <h5 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                {provider} Models
              </h5>
              <div className="space-y-2">
                {modelList.map(model => (
                  <div 
                    key={model.id} 
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      globalModels.includes(model.id)
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    } ${!canManageModels ? 'opacity-75' : ''}`}
                  >
                    {/* Global Access Checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={globalModels.includes(model.id)}
                        onChange={() => handleModelToggle(model.id)}
                        disabled={!canManageModels}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600">Global</span>
                    </label>

                    {/* Model Info */}
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                        {model.name}
                        {topModels.includes(model.id) && (
                          <Star className="w-3 h-3 text-yellow-500 fill-current" />
                        )}
                      </div>
                      {model.description && (
                        <div className="text-xs text-gray-500">{model.description}</div>
                      )}
                    </div>

                    {/* Top Model Star Button */}
                    <button
                      onClick={() => handleTopModelToggle(model.id)}
                      disabled={!canManageModels || (topModels.length >= 6 && !topModels.includes(model.id))}
                      className={`p-1 rounded transition-colors ${
                        topModels.includes(model.id)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : topModels.length >= 6
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-400 hover:text-yellow-500 cursor-pointer'
                      } ${!canManageModels ? 'cursor-not-allowed' : ''}`}
                      title={
                        topModels.includes(model.id) 
                          ? 'Remove from top models' 
                          : topModels.length >= 6 
                            ? 'Maximum 6 top models allowed'
                            : 'Add to top models'
                      }
                    >
                      <Star className={`w-4 h-4 ${topModels.includes(model.id) ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <div>
              <strong>Global models:</strong> {globalModels.length} selected
            </div>
            <div>
              <strong>Top models:</strong> {topModels.length}/6 {topModels.length >= 6 && <span className="text-yellow-600">(Maximum reached)</span>}
            </div>
          </div>
        </div>
      </div>

      {canManageModels && (
        <div className="flex justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              saveSuccess
                ? 'bg-green-600 text-white'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {saveSuccess ? (
              <>
                <Check className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ModelsSettings;